// @ts-nocheck
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { orderSubtitleSpacingStyles } from "@store/Themes/common/mixins/order";
import { inlineSeparatorStyles, loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import { formGridProps } from "@store/Themes/common/props/form";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

export interface OrderGridProps {
    container?: GridContainerProps;
    item?: GridItemProps;
    largeItem?: GridItemProps;
}

export interface OrderProps {
    block?: BlockProps;
    subtitle?: TypographyPresentationProps;
    subtitleWithLink?: TypographyPresentationProps;
    subtitleLink?: LinkPresentationProps;
    subtitleWrapper?: FlexWrapperPresentationProps;
    grid?: OrderGridProps;
    subSection?: InjectableCss;
    subSectionTitle?: TypographyPresentationProps;
}

const subtitle: OrderProps["subtitle"] = {
    variant: "h4",
    forwardAs: "h2",
    css: css`
        ${orderSubtitleSpacingStyles()}
    `,
};

export const orderProps: OrderProps = {
    block: {
        preserveSpacing: true,
        margin: "narrow",
        variant: "separated",
        variantSide: "top",
        variantProps: {
            separated: {
                adaptToContext: true,
                border: {
                    width: pxToRem(4),
                },
            },
        },
    },
    subtitle,
    subtitleWithLink: {
        ...subtitle,
    },
    subtitleLink: {
        css: linkActionStyles(),
    },
    subtitleWrapper: {
        verticalGap: pxToRem(3),
        extendedStyles: {
            wrapper: {
                css: orderSubtitleSpacingStyles(),
            },
            item: {
                css: css`
                    ${inlineSeparatorStyles({ spacing: pxToRem(23) })}
                `,
            },
        },
    },
    grid: {
        container: formGridProps.gridContainer,
        item: {
            width: [12, 6, 6, 5, 5],
            css: css`
                display: block;
            `,
        },
        largeItem: {
            width: [12, 12, 12, 10, 10],
            css: css`
                display: block;
            `,
        },
    },
    subSection: {
        css: css`
            ${breakpointValues(pxToRemArray([25, null, 30, null, 40]), "margin-bottom")}

            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    subSectionTitle: {
        ...subtitle,
        forwardAs: "h3",
        css: css`
            ${subtitle.css}
            font-size: ${x => x.theme.design.typography.size.large};
        `,
    },
};

export interface OrderSummaryProps {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    itemWrapper?: InjectableCss;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    itemLabel?: TypographyProps;
    itemValue?: TypographyProps;
    realTimeText?: TypographyPresentationProps;
}

const itemLabel: TypographyProps = {
    css: css`
        display: table-cell;
    `,
};

const itemValue: TypographyProps = {
    css: css`
        display: table-cell;
        text-align: right;
        padding-left: ${pxToRem(10)};
    `,
};

export const orderSummaryProps: OrderSummaryProps = {
    wrapper: {
        css: css`
            ${boxStyles(pxToRemArray([20]))}
            display: block;
            width: 100%;
        `,
    },
    titleWrapper: {
        css: css`
            min-width: 100%;
            ${spacingStyles(null, pxToRem(8), "padding")}
        `,
    },
    title: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    itemWrapper: {
        css: css`
            display: table;
            width: 100%;
            ${spacingStyles(pxToRem(8), pxToRem(8), "padding")}
            font-size: ${x => x.theme.design.typography.size.smaller};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};

            &:last-child {
                border-bottom: 0;
            }
        `,
    },
    totalLabel: {
        ...itemLabel,
        css: css`
            ${itemLabel.css}
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    totalValue: {
        ...itemValue,
        css: css`
            ${itemValue.css}
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    itemLabel,
    itemValue,
    realTimeText: {
        css: loadingDotsStyles(),
    },
};
